/**
 * Split a party's EORI into the tin + Niederlassungsnummer ATLAS wants.
 *
 * A German EORI is "DE" + a 7-digit Zollnummer. The 4-digit
 * Niederlassungsnummer is a SEPARATE field, and ATLAS wants them apart: the
 * ACCEPTED declaration 26DE586601351954A3 prints its declarant and exporter ID
 * as "DE9000127" — nine characters — with the branch in its own box.
 *
 * BEO's getClientDetails hands out the same EORI joined, as "DE90001270000",
 * and this page injected that verbatim into kopf.anmelder.tin. The backend
 * splits it again at the payload boundary, so submission was correct — but the
 * FORM still showed the 13-character value and had to be corrected by hand on
 * every declaration.
 *
 * Mirrors split_party_eori in app/services/atlas_defaults.py. Only the German
 * shape is split; a foreign EORI is returned whole, because guessing where one
 * ends would corrupt a value that is already right.
 */
const GERMAN_EORI_WITH_BRANCH = /^([A-Z]{2}\d{7})(\d{4})$/;

export const DEFAULT_BRANCH_NUMBER = '0000';

export interface PartyEori {
  tin: string;
  niederlassungsNummer: string;
}

export function splitPartyEori(
  eori: string | null | undefined,
  fallbackBranch: string = DEFAULT_BRANCH_NUMBER,
): PartyEori {
  const text = String(eori ?? '').trim();
  if (!text) {
    return { tin: '', niederlassungsNummer: fallbackBranch };
  }
  const match = GERMAN_EORI_WITH_BRANCH.exec(text);
  if (match) {
    return { tin: match[1], niederlassungsNummer: match[2] };
  }
  return { tin: text, niederlassungsNummer: fallbackBranch };
}

/**
 * Fill a party object's tin and branch from a configured EORI, without
 * overwriting anything already present.
 */
export function applyPartyEori(
  party: Record<string, any>,
  eori: string | null | undefined,
): void {
  const { tin, niederlassungsNummer } = splitPartyEori(eori);
  if (!party.tin && tin) {
    party.tin = tin;
    if (!party.niederlassungsNummer) {
      party.niederlassungsNummer = niederlassungsNummer;
    }
  }
}
